import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import { conteneurQuiDefile, remonterEnHaut } from '../utils/defilement';

/**
 * Le bouton rond qui ramène en haut, en bas à droite de l'écran.
 *
 * Il ne porte pas la mécanique — elle est dans utils/defilement, partagée avec
 * le bouton du bas des guides. Il ne décide que de deux choses : quand se
 * montrer, et sur quoi écouter le défilement.
 */
export default function RetourEnHaut() {
  const [visible, setVisible] = useState(false);
  const conteneur = useRef<HTMLElement | Window | null>(null);
  const emplacement = useLocation();

  const trouverConteneur = useCallback(() => conteneurQuiDefile(), []);

  const positionActuelle = (cible: HTMLElement | Window): number =>
    cible instanceof Window ? window.scrollY : cible.scrollTop;

  useEffect(() => {
    let cible = trouverConteneur();
    conteneur.current = cible;

    /*
     * Le seuil : une hauteur d'écran.
     *
     * Il était d'une hauteur et demie, ce qui, sur un portable de neuf cents
     * pixels, demandait treize cent cinquante pixels de descente avant que la
     * flèche paraisse — assez pour qu'on la croie absente. Un écran entier
     * suffit à écarter les pages courtes, où remonter se fait d'un geste.
     */
    const seuil = () => Math.max(320, window.innerHeight);

    const surDefilement = () => setVisible(positionActuelle(cible) > seuil());

    const brancher = (nouvelle: HTMLElement | Window) => {
      cible = nouvelle;
      conteneur.current = nouvelle;
      nouvelle.addEventListener('scroll', surDefilement, { passive: true });
      surDefilement();
    };

    const debrancher = () => cible.removeEventListener('scroll', surDefilement);

    brancher(cible);

    /*
     * Ce qui rend la page longue arrive après le premier rendu : une liste
     * chargée par le réseau, une image, une notice qu'on déplie. Rien ne le
     * signale au bouton, d'où ces deux observateurs.
     *
     * Le corps du document grandit avec le contenu, puisque c'est la fenêtre
     * qui défile : l'observer suffit aujourd'hui. Les mutations sont surveillées
     * en plus, pour le cas où la mise en page reviendrait à un conteneur
     * interne — le corps garderait alors une taille fixe, et lui seul ne dirait
     * plus rien.
     */
    const reevaluer = () => {
      const maintenant = trouverConteneur();
      if (maintenant !== cible) {
        debrancher();
        brancher(maintenant);
      } else {
        surDefilement();
      }
    };

    const tailles = new ResizeObserver(reevaluer);
    tailles.observe(document.body);

    const mutations = new MutationObserver(reevaluer);
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      debrancher();
      tailles.disconnect();
      mutations.disconnect();
    };
  }, [emplacement.pathname, trouverConteneur]);

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={() => remonterEnHaut()}
      aria-label="Revenir en haut de la page"
      title="Revenir en haut"
      // bottom-20 sur mobile : la barre du bas occupe le coin, et un bouton posé
      // dessus se toucherait à sa place. Sur grand écran, il n'y a pas de barre
      // — le bouton redescend.
      className="
        fixed right-4 bottom-20 lg:right-8 lg:bottom-8 z-50
        flex h-12 w-12 items-center justify-center
        rounded-full bg-primary-600 text-white
        shadow-lg ring-1 ring-black/5
        transition hover:bg-primary-700 hover:shadow-xl
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2
      "
    >
      <ArrowUp className="h-6 w-6" aria-hidden="true" />
    </button>
  );
}
